import { Input } from "@pushpress/pushpress-ui";
import { TextInputType } from "../constants/signup.types";
import { z } from "zod";
import { useState } from "react";
import { useValidateEmailQuery } from "../../../apis/authApi.hooks";
import LockIcon from "./LockIcon";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { SignUpEvent } from "../constants/signup.events";

// Basic URL validation - Zod URL validation is too strict
// Text must contain at least `<someString>.<someOtherString>`
const basicUrlRegex = /[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+/;

const getValidationSchema = (type: TextInputType) => {
  switch (type) {
    case "email":
      return z.string().min(1, "Required").email("Invalid email address");
    case "phone":
      return z
        .string()
        .min(1, "Required")
        .regex(
          /^\+?[1-9]\d{1,14}$/,
          "Invalid phone number (input digits only)",
        );
    case "url":
      return z
        .string()
        .optional()
        .transform((str) => str?.trim() || "")
        .refine(
          (str) => str === "" || basicUrlRegex.test(str),
          "Invalid URL format",
        );
    default:
      return z.string().min(2, "Required");
  }
};

export const isValidTextInput = (value: string, inputType: TextInputType) => {
  const schema = getValidationSchema(inputType);
  try {
    schema.parse(value);
    return true;
  } catch (err) {
    return false;
  }
};

interface TextInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  inputType: TextInputType;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onEnable?: () => void;
}

const TextInput = ({
  id,
  label,
  value,
  onChange,
  inputType,
  placeholder,
  required,
  disabled,
  onEnable,
}: TextInputProps) => {
  const { trackEvent } = useAnalytics();
  const [error, setError] = useState<string | null>(null);
  const validateEmail = useValidateEmailQuery();

  const errorStyling = error
    ? "tw-border-red-500 focus-visible:tw-ring-red-500"
    : "";

  const backgroundColor = disabled ? "tw-bg-gray-200" : "tw-bg-white";

  const handleBlur = async () => {
    try {
      const schema = getValidationSchema(inputType);
      schema.parse(value);

      if (inputType === "email") {
        await validateEmail.mutateAsync(
          { email: value },
          {
            onSuccess: () => {
              setError(null);
            },
            onError: () => {
              setError("Invalid email address");
              trackEvent(SignUpEvent.EmailValidationServiceCallFailed, {
                location: "Single Activation Flow Signup",
              });
            },
          },
        );
      }

      setError(null);
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      }
    }
  };

  return (
    <div className="tw-space-y-2">
      <label htmlFor={id} className="tw-block tw-text-sm tw-font-medium">
        {label}
      </label>
      <div className="tw-relative">
        <Input
          id={id}
          className={`tw-h-[64px] !tw-text-[18px] ${backgroundColor} tw-w-full ${errorStyling}`}
          type={inputType === "password" ? "password" : "text"}
          value={value || ""}
          onChange={(e) => {
            if (!disabled) {
              setError(null);
              onChange(e.target.value);
            }
          }}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          readOnly={disabled}
          disabled={disabled}
        />
        {disabled && onEnable && <LockIcon onUnlock={onEnable} />}
      </div>
      {error && <p className="tw-text-red-500 tw-text-sm">{error}</p>}
    </div>
  );
};

export default TextInput;
